import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Sparkles,
  Send,
  Copy,
  Check,
  Wand2,
  FileText,
  ShoppingCart,
  Search,
  MessageSquare,
  Loader2,
  RefreshCw
} from 'lucide-react';

const aiTypes = [
  { id: 'general', label: 'Assistente Generale', icon: MessageSquare, description: 'Chiedi qualsiasi cosa' },
  { id: 'copywriting', label: 'Copywriting', icon: FileText, description: 'Testi marketing, titoli' },
  { id: 'sales', label: 'Assistente Vendite', icon: ShoppingCart, description: 'Pitch, follow-up' },
  { id: 'seo', label: 'Ottimizzatore SEO', icon: Search, description: 'Keyword, meta descrizioni' },
];

const samplePrompts = {
  general: [
    'Aiutami a scrivere un\'email professionale per un cliente',
    'Suggerisci idee per migliorare la fidelizzazione dei clienti',
    'Crea un modello di agenda per riunioni settimanali'
  ],
  copywriting: [
    'Scrivi un titolo accattivante per il lancio di un nuovo prodotto',
    'Crea 3 varianti di una caption Instagram per una promozione',
    'Scrivi la descrizione di un prodotto per cuffie wireless'
  ],
  sales: [
    'Scrivi un\'email di follow-up per un potenziale cliente',
    'Crea un messaggio di outreach per LinkedIn',
    'Suggerisci risposte alle obiezioni comuni di vendita'
  ],
  seo: [
    'Genera una meta descrizione per un sito di un bar',
    'Suggerisci keyword per un e-commerce di moda',
    'Ottimizza questo titolo prodotto per i motori di ricerca'
  ],
};

const AIAssistantPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [selectedType, setSelectedType] = useState('general');
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setResponse('');
    
    try {
      const result = await axios.post(`${API}/ai/generate`, 
        { prompt, type: selectedType },
        getAuthHeaders()
      );
      setResponse(result.data.response);
      setHistory(prev => [{
        prompt,
        response: result.data.response,
        type: selectedType,
        timestamp: new Date().toISOString()
      }, ...prev].slice(0, 10));
    } catch (error) {
      setResponse('Mi dispiace, si è verificato un errore. Riprova.');
      console.error('Errore generazione IA:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUseSamplePrompt = (sample) => {
    setPrompt(sample);
  };

  const selectedTypeData = aiTypes.find(t => t.id === selectedType);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="ai-assistant-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-outfit">Assistente IA</h1>
        <p className="text-muted-foreground mt-1">Alimentato da GPT-5.2 per generazione contenuti intelligente</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Pannello Principale */}
        <div className="lg:col-span-8 space-y-6">
          {/* Selezione Tipo */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {aiTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`p-4 rounded-lg border transition-all text-left ${
                      selectedType === type.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`ai-type-${type.id}`}
                  >
                    <type.icon className={`w-5 h-5 mb-2 ${selectedType === type.id ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{type.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Input */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  {selectedTypeData && <selectedTypeData.icon className="w-4 h-4 text-primary" />}
                </div>
                <CardTitle className="text-lg font-outfit">{selectedTypeData?.label}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`Inserisci la tua richiesta per ${selectedTypeData?.label.toLowerCase()}...`}
                rows={4}
                className="resize-none"
                data-testid="ai-prompt-input"
              />
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {samplePrompts[selectedType].slice(0, 2).map((sample) => (
                    <Button 
                      key={sample}
                      variant="outline" 
                      size="sm"
                      onClick={() => handleUseSamplePrompt(sample)}
                      className="text-xs"
                    >
                      <Wand2 className="w-3 h-3 mr-1" />
                      {sample.slice(0, 30)}...
                    </Button>
                  ))}
                </div>
                <Button 
                  onClick={handleGenerate} 
                  disabled={loading || !prompt.trim()}
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generazione...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Genera
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Risposta */}
          <Card className={response ? 'ring-1 ring-primary/20' : ''}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-outfit">Risposta</CardTitle>
                {response && (
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleGenerate()}
                      disabled={loading}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      Rigenera
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCopy}
                      data-testid="copy-response-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-3.5 h-3.5 mr-1" />
                          Copiato!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 mr-1" />
                          Copia
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Generazione del contenuto in corso...</p>
                  </div>
                </div>
              ) : response ? (
                <div 
                  className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap"
                  data-testid="ai-response"
                >
                  {response}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Sparkles className="w-12 h-12 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Il contenuto generato dall'IA apparirà qui</p>
                  <p className="text-sm text-muted-foreground mt-1">Seleziona un tipo e inserisci la tua richiesta sopra</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Cronologia */}
        <div className="lg:col-span-4">
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="text-lg font-outfit">Generazioni Recenti</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {history.length > 0 ? (
                  <div className="divide-y divide-border">
                    {history.map((item) => {
                      const typeData = aiTypes.find(t => t.id === item.type);
                      return (
                        <div 
                          key={item.id || `${item.type}-${item.prompt?.slice(0,20)}`}
                          className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => {
                            setPrompt(item.prompt);
                            setResponse(item.response);
                            setSelectedType(item.type);
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary" className="text-xs">
                              {typeData?.label}
                            </Badge>
                          </div>
                          <p className="text-sm font-medium line-clamp-1">{item.prompt}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{item.response}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Nessuna cronologia</p>
                    <p className="text-xs text-muted-foreground mt-1">Le tue generazioni recenti appariranno qui</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
